import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Button,
  HStack,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ArrowLeftIcon, CopyIcon, Warning } from '@phosphor-icons/react';
import { IManualEntryFieldSelected } from '../../../../models/m6-2';

// ─── Roster mock ──────────────────────────────────────────────────────
// Placeholder: integrazione multi-atleta futura (Sprint 6).

interface IRosterAthlete {
  _id: string;
  firstName: string;
  lastName: string;
}

const MOCK_ROSTER: IRosterAthlete[] = [
  { _id: 'athlete-1', firstName: 'Atleta', lastName: 'Uno' },
  { _id: 'athlete-2', firstName: 'Atleta', lastName: 'Due' },
  { _id: 'athlete-3', firstName: 'Atleta', lastName: 'Tre' },
];

// ─── Field type helpers ───────────────────────────────────────────────

const NUMBER_FIELDS = new Set([
  'duration', 'srpe', 'session_load', 'distance_km', 'steps',
  'sets', 'reps', 'hr_avg', 'hr_max', 'speed_avg_kmh', 'power_avg_w',
  'kcal', 'vo2max_estimate', 'wellness_pre', 'wellness_post',
]);

function isNumberField(canonicalKey: string): boolean {
  return NUMBER_FIELDS.has(canonicalKey);
}

function isRpeField(canonicalKey: string): boolean {
  return canonicalKey === 'rpe';
}

function isSliderField(canonicalKey: string): boolean {
  return isRpeField(canonicalKey) || canonicalKey === 'wellness_pre' || canonicalKey === 'wellness_post';
}

function fieldLabel(canonicalKey: string): string {
  const labels: Record<string, string> = {
    date: 'Data',
    duration: 'Durata (min)',
    rpe: 'RPE (0-10)',
    srpe: 'sRPE',
    session_load: 'Session Load',
    distance_km: 'Distanza (km)',
    steps: 'Passi',
    sets: 'Serie',
    reps: 'Ripetizioni',
    hr_avg: 'FC media (bpm)',
    hr_max: 'FC max (bpm)',
    speed_avg_kmh: 'Velocità media (km/h)',
    power_avg_w: 'Potenza media (W)',
    kcal: 'Kcal',
    vo2max_estimate: 'VO2max stimato',
    notes: 'Note',
    wellness_pre: 'Benessere pre (0-10)',
    wellness_post: 'Benessere post (0-10)',
  };
  return labels[canonicalKey] ?? canonicalKey;
}

// ─── RPE Slider ───────────────────────────────────────────────────────

interface RpeSliderProps {
  value: number;
  onChange: (v: number) => void;
  label?: string;
}

const RpeSlider: React.FC<RpeSliderProps> = ({ value, onChange, label }) => {
  const bgColor =
    value <= 3 ? '#48bb78' : value <= 6 ? '#ed8936' : '#fc8181';

  return (
    <VStack align="stretch" gap={1}>
      {label && (
        <Text fontSize="xs" fontWeight="semibold" color="gray.600">
          {label}
        </Text>
      )}
      <HStack gap={3} align="center">
        <Box flex={1}>
          <input
            type="range"
            min={0}
            max={10}
            step={0.5}
            value={value}
            onChange={(e) => onChange(parseFloat(e.target.value))}
            style={{ width: '100%', accentColor: bgColor }}
          />
        </Box>
        <Box
          w={10}
          h={8}
          display="flex"
          alignItems="center"
          justifyContent="center"
          style={{ backgroundColor: bgColor }}
          borderRadius="md"
        >
          <Text fontSize="sm" fontWeight="bold" color="white">
            {value.toFixed(1)}
          </Text>
        </Box>
      </HStack>
    </VStack>
  );
};

// ─── Session-level field input ────────────────────────────────────────

interface SessionFieldInputProps {
  field: IManualEntryFieldSelected;
  value: string | number;
  onChange: (v: string | number) => void;
}

const SessionFieldInput: React.FC<SessionFieldInputProps> = ({ field, value, onChange }) => {
  const label = fieldLabel(field.canonicalKey);

  if (field.canonicalKey === 'date') {
    return (
      <VStack align="stretch" gap={1}>
        <Text fontSize="xs" fontWeight="semibold" color="gray.600">
          {label}
        </Text>
        <Input
          type="date"
          size="sm"
          value={value as string}
          onChange={(e) => onChange(e.target.value)}
        />
      </VStack>
    );
  }

  if (field.canonicalKey === 'notes') {
    return (
      <VStack align="stretch" gap={1}>
        <Text fontSize="xs" fontWeight="semibold" color="gray.600">
          {label}
        </Text>
        <textarea
          value={value as string}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          placeholder="Note sessione..."
          style={{
            padding: '8px 12px',
            border: '1px solid #e2e8f0',
            borderRadius: 6,
            fontSize: 14,
            resize: 'vertical',
            fontFamily: 'inherit',
          }}
        />
      </VStack>
    );
  }

  if (isSliderField(field.canonicalKey)) {
    return (
      <RpeSlider
        label={label}
        value={value as number}
        onChange={onChange}
      />
    );
  }

  return (
    <VStack align="stretch" gap={1}>
      <Text fontSize="xs" fontWeight="semibold" color="gray.600">
        {label}
      </Text>
      <Input
        size="sm"
        type={isNumberField(field.canonicalKey) ? 'number' : 'text'}
        value={value as string}
        onChange={(e) => onChange(isNumberField(field.canonicalKey) ? parseFloat(e.target.value) || 0 : e.target.value)}
        placeholder={label}
      />
    </VStack>
  );
};

// ─── Per-athlete section ──────────────────────────────────────────────

interface PerAthleteValue {
  [athleteId: string]: number;
}

interface PerAthleteSectionProps {
  field: IManualEntryFieldSelected;
  values: PerAthleteValue;
  onChange: (athleteId: string, v: number) => void;
  onApplyAll: (v: number) => void;
}

const PerAthleteSection: React.FC<PerAthleteSectionProps> = ({
  field,
  values,
  onChange,
  onApplyAll,
}) => {
  const label = fieldLabel(field.canonicalKey);
  const isRpe = isRpeField(field.canonicalKey);

  // Applica valore del primo atleta a tutti
  const firstVal = values[MOCK_ROSTER[0]?._id] ?? 5;

  return (
    <Box borderWidth={1} borderColor="gray.200" borderRadius="md" overflow="hidden">
      <HStack
        px={3}
        py={2}
        bg="gray.50"
        borderBottomWidth={1}
        borderBottomColor="gray.200"
        justify="space-between"
      >
        <Text fontSize="xs" fontWeight="semibold" color="gray.600" textTransform="uppercase">
          {label} — per atleta
        </Text>
        {isRpe && (
          <Button
            size="2xs"
            variant="ghost"
            colorPalette="blue"
            onClick={() => onApplyAll(firstVal)}
          >
            <HStack gap={1}>
              <CopyIcon size={12} />
              <Text>Applica stesso a tutti</Text>
            </HStack>
          </Button>
        )}
      </HStack>

      <VStack align="stretch" gap={0}>
        {MOCK_ROSTER.map((athlete, idx) => {
          const val = values[athlete._id] ?? (isRpe ? 5 : 0);

          return (
            <HStack
              key={athlete._id}
              px={3}
              py={2}
              gap={4}
              align="center"
              borderTopWidth={idx > 0 ? 1 : 0}
              borderTopColor="gray.100"
            >
              <Text fontSize="sm" flex={1} minW={0} lineClamp={1}>
                {athlete.firstName} {athlete.lastName}
              </Text>

              <Box flex={1}>
                {isRpe ? (
                  <RpeSlider
                    value={val}
                    onChange={(v) => onChange(athlete._id, v)}
                  />
                ) : (
                  <Input
                    size="sm"
                    type="number"
                    value={val}
                    onChange={(e) => onChange(athlete._id, parseFloat(e.target.value) || 0)}
                    placeholder="0"
                  />
                )}
              </Box>
            </HStack>
          );
        })}
      </VStack>

      <Box px={3} py={1} bg="yellow.50" borderTopWidth={1} borderTopColor="yellow.100">
        <Text fontSize="xs" color="yellow.700">
          Placeholder roster — integrazione multi-atleta reale in Sprint 6
        </Text>
      </Box>
    </Box>
  );
};

// ─── Outlier detection ───────────────────────────────────────────────

/**
 * Rileva valori anomali nel payload del form (soft warning, non bloccante).
 * Controlla RPE collettivo, durata eccessiva e distanza totale.
 */
function detectOutliers(data: Record<string, unknown>): string[] {
  const warnings: string[] = [];

  const rpeValues = Object.entries(data)
    .filter(([k]) => k.startsWith('rpe_'))
    .map(([, v]) => Number(v))
    .filter((n) => !isNaN(n));
  if (rpeValues.length >= 3 && rpeValues.every((v) => v >= 9)) {
    warnings.push('RPE ≥9 per tutti gli atleti — verifica correttezza');
  }

  const duration = Number(data.session_duration ?? 0);
  if (duration > 300) warnings.push('Durata > 5 ore — verifica');

  const td = Number(data.td ?? 0);
  if (td > 20000) warnings.push('Distanza totale > 20km — verifica');

  return warnings;
}

// ─── Props ────────────────────────────────────────────────────────────

interface ManualEntryFormProps {
  /** Campi selezionati nello Step A (ManualEntryFieldSelector) */
  fields: IManualEntryFieldSelected[];
  /** Callback submit con i valori raccolti */
  onSubmit: (data: Record<string, unknown>) => void;
  /** Torna allo Step A */
  onBack: () => void;
}

// ─── Main component ───────────────────────────────────────────────────

/**
 * Step B wizard input manuale M6.2 B13 — form dinamico basato sui campi selezionati.
 * - level='session': input singolo (numero/testo/slider/date)
 * - level='per_athlete': tabella atleti con input per riga (placeholder roster mock)
 * - Slider RPE 0-10 per per_athlete + shortcut "Applica stesso a tutti"
 * - Submit → placeholder save (integrazione reale in Sprint 6 con endpoint /sessions/manual)
 *
 * Componente standalone: non integrato in pagina — pronto per uso futuro wizard.
 */
export const ManualEntryForm: React.FC<ManualEntryFormProps> = ({
  fields,
  onSubmit,
  onBack,
}) => {
  const today = new Date().toISOString().slice(0, 10);

  // Stato valori session-level
  const [sessionValues, setSessionValues] = useState<Record<string, string | number>>(() => {
    const init: Record<string, string | number> = {};
    fields
      .filter((f) => f.level === 'session')
      .forEach((f) => {
        if (f.canonicalKey === 'date') init[f.canonicalKey] = today;
        else if (isSliderField(f.canonicalKey)) init[f.canonicalKey] = 5;
        else if (isNumberField(f.canonicalKey)) init[f.canonicalKey] = 0;
        else init[f.canonicalKey] = '';
      });
    return init;
  });

  // Stato valori per_athlete (per canonicalKey → athleteId → valore)
  const [perAthleteValues, setPerAthleteValues] = useState<Record<string, PerAthleteValue>>(() => {
    const init: Record<string, PerAthleteValue> = {};
    fields
      .filter((f) => f.level === 'per_athlete')
      .forEach((f) => {
        init[f.canonicalKey] = {};
        MOCK_ROSTER.forEach((a) => {
          init[f.canonicalKey][a._id] = isSliderField(f.canonicalKey) ? 5 : 0;
        });
      });
    return init;
  });

  const setSessionField = useCallback((key: string, value: string | number) => {
    setSessionValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setPerAthleteField = useCallback(
    (canonicalKey: string, athleteId: string, value: number) => {
      setPerAthleteValues((prev) => ({
        ...prev,
        [canonicalKey]: { ...(prev[canonicalKey] ?? {}), [athleteId]: value },
      }));
    },
    [],
  );

  const applyAllAthletesValue = useCallback((canonicalKey: string, value: number) => {
    setPerAthleteValues((prev) => {
      const perKey: PerAthleteValue = {};
      MOCK_ROSTER.forEach((a) => { perKey[a._id] = value; });
      return { ...prev, [canonicalKey]: perKey };
    });
  }, []);

  const [submitting, setSubmitting] = useState(false);

  // Calcola warning outlier in base ai valori correnti
  const outlierWarnings = useMemo(() => {
    const flatData: Record<string, unknown> = {
      ...sessionValues,
      ...Object.fromEntries(
        Object.entries(perAthleteValues).flatMap(([key, perAthlete]) =>
          Object.entries(perAthlete).map(([athleteId, val]) => [`${key}_${athleteId}`, val]),
        ),
      ),
    };
    return detectOutliers(flatData);
  }, [sessionValues, perAthleteValues]);

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    try {
      const data: Record<string, unknown> = {
        ...sessionValues,
        perAthlete: perAthleteValues,
      };
      // Placeholder: integrazione con endpoint POST /sessions/manual in Sprint 6
      await onSubmit(data);
    } finally {
      setSubmitting(false);
    }
  }, [sessionValues, perAthleteValues, onSubmit]);

  const sessionFields = fields.filter((f) => f.level === 'session');
  const perAthleteFields = fields.filter((f) => f.level === 'per_athlete');

  return (
    <VStack align="stretch" gap={5}>
      {/* Session-level fields */}
      {sessionFields.length > 0 && (
        <Box>
          <Text
            fontSize="xs"
            fontWeight="semibold"
            color="gray.500"
            textTransform="uppercase"
            letterSpacing="wider"
            mb={3}
          >
            Dati sessione
          </Text>
          <VStack align="stretch" gap={4}>
            {sessionFields.map((field) => (
              <SessionFieldInput
                key={field.canonicalKey}
                field={field}
                value={sessionValues[field.canonicalKey] ?? ''}
                onChange={(v) => setSessionField(field.canonicalKey, v)}
              />
            ))}
          </VStack>
        </Box>
      )}

      {/* Per-athlete fields */}
      {perAthleteFields.length > 0 && (
        <Box>
          <Text
            fontSize="xs"
            fontWeight="semibold"
            color="gray.500"
            textTransform="uppercase"
            letterSpacing="wider"
            mb={3}
          >
            Dati per atleta
          </Text>
          <VStack align="stretch" gap={4}>
            {perAthleteFields.map((field) => (
              <PerAthleteSection
                key={field.canonicalKey}
                field={field}
                values={perAthleteValues[field.canonicalKey] ?? {}}
                onChange={(athleteId, v) =>
                  setPerAthleteField(field.canonicalKey, athleteId, v)
                }
                onApplyAll={(v) => applyAllAthletesValue(field.canonicalKey, v)}
              />
            ))}
          </VStack>
        </Box>
      )}

      {/* Outlier warnings — soft, non bloccante */}
      {outlierWarnings.length > 0 && (
        <Box
          p={3}
          bg="yellow.50"
          borderWidth={1}
          borderColor="yellow.300"
          borderRadius="md"
        >
          <HStack gap={2} mb={1} align="center">
            <Warning size={16} color="#b7791f" />
            <Text fontSize="xs" fontWeight="semibold" color="yellow.800">
              Attenzione — valori anomali rilevati
            </Text>
          </HStack>
          <VStack align="stretch" gap={1}>
            {outlierWarnings.map((w, i) => (
              <Text key={i} fontSize="xs" color="yellow.700">
                • {w}
              </Text>
            ))}
          </VStack>
        </Box>
      )}

      {/* Actions */}
      <HStack gap={3} pt={2}>
        <Button variant="outline" onClick={onBack} flex={1}>
          <HStack gap={1}>
            <ArrowLeftIcon size={16} />
            <Text>Modifica campi</Text>
          </HStack>
        </Button>
        <Button
          colorPalette="green"
          onClick={handleSubmit}
          loading={submitting}
          loadingText="Salvataggio..."
          flex={2}
        >
          Salva sessione
        </Button>
      </HStack>

      <Box
        p={3}
        bg="orange.50"
        borderWidth={1}
        borderColor="orange.200"
        borderRadius="md"
      >
        <Text fontSize="xs" color="orange.700">
          Integrazione endpoint /sessions/manual · Sprint 6. Submit chiama attualmente il callback onSubmit con i dati raccolti.
        </Text>
      </Box>
    </VStack>
  );
};
